import React from "react";
import { Link } from "react-router-dom";
import { track } from "@vercel/analytics";
import {
  formatBoardDate,
  formatBoardMoney,
  houseLine,
} from "../../utils/homepageSeason";
import { tournamentPath } from "../../utility/tournamentUrls";

const proofCard =
  "rounded-lg bg-brand-cream p-5 md:p-6 border border-brand-charcoal/15";
const deskButton =
  "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-display font-bold tracking-wide text-brand-charcoal bg-white border border-brand-charcoal/20 hover:border-brand-gold-ink";
const bone = "rounded bg-brand-charcoal/10 animate-pulse";

const HeroScoreboardSkeleton = () => (
  <div className={proofCard} aria-hidden="true">
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex-1">
        <div className={`h-7 w-2/3 ${bone}`} />
        <div className={`h-4 w-1/2 mt-2 ${bone}`} />
      </div>
      <div className={`h-6 w-24 ${bone}`} />
    </div>
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((row) => (
        <div key={row} className="flex items-center gap-3">
          <div className={`h-5 w-6 ${bone}`} />
          <div className={`h-5 flex-1 ${bone}`} />
          <div className={`h-5 w-12 ${bone}`} />
          <div className={`h-5 w-14 ${bone}`} />
        </div>
      ))}
    </div>
    <div className="mt-4 pt-3 border-t border-brand-charcoal/10">
      <div className={`h-9 w-28 ${bone}`} />
    </div>
  </div>
);

const HeroScoreboard = ({
  tournament,
  rows = [],
  live,
  prizeFund = null,
  loading = false,
}) => {
  if (loading) {
    return <HeroScoreboardSkeleton />;
  }

  if (!tournament || rows.length === 0) {
    return null;
  }

  const label = live ? "On the lanes" : "Final standings";
  const showPrize = rows.some((row) => row.prize != null);
  const houseLabel = houseLine(tournament);

  return (
    <div className={proofCard}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="font-display tracking-widest uppercase text-xs text-brand-gold-ink">
            The board
          </p>
          <h2 className="heading-display text-2xl mt-1">{tournament.name}</h2>
          <p className="text-brand-muted text-sm mt-1">
            {[
              houseLabel,
              formatBoardDate(tournament.lastDate || tournament.startDate),
              prizeFund ? `${formatBoardMoney(prizeFund)} prize fund` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <span
          className={`shrink-0 font-display text-xs tracking-widest uppercase px-2 py-1 rounded-lg ${
            live
              ? "bg-brand-gold text-brand-charcoal"
              : "bg-brand-charcoal text-brand-cream"
          }`}
        >
          {label}
        </span>
      </div>

      <table className="w-full text-left font-score">
        <thead>
          <tr className="text-brand-muted text-xs uppercase tracking-wider">
            <th className="py-1 pr-2 font-medium w-8"></th>
            <th className="py-1 pr-2 font-medium">
              {rows[0]?.eventName || "Team"}
            </th>
            <th
              className={`py-1 font-medium text-right ${
                showPrize ? "pr-2" : ""
              }`}
            >
              Score
            </th>
            {showPrize ? (
              <th className="py-1 font-medium text-right">Prize</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.position}-${row.name}`}
              className="border-t border-brand-charcoal/10"
            >
              <td className="py-2 pr-2 heading-display text-lg">
                {row.position}
              </td>
              <td className="py-2 pr-2 text-brand-charcoal">{row.name}</td>
              <td
                className={`py-2 text-right text-brand-charcoal ${
                  showPrize ? "pr-2" : ""
                }`}
              >
                {row.score}
              </td>
              {showPrize ? (
                <td className="py-2 text-right text-brand-charcoal">
                  {row.prize != null ? formatBoardMoney(row.prize) : ""}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 pt-3 border-t border-brand-charcoal/10">
        <Link
          to={tournamentPath(tournament, "/standings")}
          onClick={() =>
            track("full_results_click", { location: "hero_scoreboard" })
          }
          className={deskButton}
        >
          Full Standings
        </Link>
      </div>
    </div>
  );
};

export default HeroScoreboard;
